import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";

import { api } from "@/api/client";
import { deriveScenes } from "@/data/comics";

/**
 * Client-side PDF export for a comic issue.
 * Layout mirrors the reader:
 *   - Cover: colored full-bleed page + title + AI cover image
 *   - Story pages: chapter title, narrator caption, AI hero image with
 *     speech bubbles DRAWN ON TOP of the image (Kalimán-style), lesson callout.
 * Bubbles are drawn as native jsPDF shapes so text stays sharp on the PDF.
 */
export default function PdfComicButton({ comic, lang, testid = "comic-pdf-btn" }) {
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: illList } = await api.get(`/comics/${comic.slug}/illustrations`);
      const illMap = {};
      for (const it of illList) {
        const p = it.page_index || 0;
        const s = it.scene_index || 0;
        if (!illMap[p]) illMap[p] = {};
        illMap[p][s] = it;
      }

      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const W = pdf.internal.pageSize.getWidth();
      const H = pdf.internal.pageSize.getHeight();
      const M = 32;

      /* ── Speech bubble drawn directly on image ── */
      const drawBubble = (x, y, w, speaker, text) => {
        const name =
          speaker === "ana" ? "ANA" :
          speaker === "carlos" ? "CARLOS" :
          speaker === "mascot" ? "SOFY" : "";
        const tone =
          speaker === "carlos" ? [255, 240, 230] :
          speaker === "mascot" ? [255, 248, 214] :
          [234, 243, 255];
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        const lines = pdf.splitTextToSize(text, w - 12);
        const bodyH = lines.length * 12 + 12;
        // shadow
        pdf.setFillColor(0, 0, 0);
        pdf.rect(x + 3, y + 3, w, bodyH, "F");
        // body
        pdf.setFillColor(...tone);
        pdf.rect(x, y, w, bodyH, "F");
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(1.5);
        pdf.rect(x, y, w, bodyH);
        // name tag
        if (name) {
          const tagW = name.length * 5.5 + 10;
          pdf.setFillColor(255, 209, 0);
          pdf.rect(x + 8, y - 8, tagW, 12, "F");
          pdf.rect(x + 8, y - 8, tagW, 12);
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(8);
          pdf.text(name, x + 8 + tagW / 2, y + 1, { align: "center" });
        }
        // text
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(lines, x + 6, y + 14);
      };

      const drawCaptionStrip = (x, y, w, text) => {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        const lines = pdf.splitTextToSize(text, w - 12);
        const boxH = lines.length * 12 + 10;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(x, y - boxH, w, boxH, "F");
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(2);
        pdf.rect(x, y - boxH, w, boxH);
        pdf.setTextColor(0, 0, 0);
        pdf.text(lines, x + 6, y - boxH + 12);
        return boxH;
      };

      const drawChapterBadge = (x, y, text) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        const tw = pdf.getTextWidth(text) + 12;
        pdf.setFillColor(227, 0, 15);
        pdf.rect(x, y, tw, 14, "F");
        pdf.setDrawColor(0, 0, 0);
        pdf.rect(x, y, tw, 14);
        pdf.setTextColor(255, 255, 255);
        pdf.text(text.toUpperCase(), x + 6, y + 10);
      };

      /* Render one image + overlaid bubbles/caption/badge. Returns bottom-Y. */
      const renderImagePanel = (imgDataUrl, x, y, w, h, opts = {}) => {
        try {
          pdf.addImage(imgDataUrl, "JPEG", x, y, w, h, undefined, "FAST");
        } catch (_e) {
          pdf.setFillColor(240, 240, 240);
          pdf.rect(x, y, w, h, "F");
        }
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(2);
        pdf.rect(x, y, w, h);
        // chapter badge
        if (opts.chapter) drawChapterBadge(x + 8, y + 8, opts.chapter);
        // bubbles (list of {speaker, text, side: 'left'|'right'})
        (opts.bubbles || []).forEach((b, i) => {
          const bw = Math.min(w * 0.45, 220);
          const bx = b.side === "right" ? x + w - bw - 8 : x + 8;
          const by = y + 20 + i * 60;
          drawBubble(bx, by, bw, b.speaker, b.text);
        });
        // narrator strip at bottom of image
        if (opts.caption) drawCaptionStrip(x, y + h, w, opts.caption);
      };

      comic.pages.forEach((page, idx) => {
        if (idx > 0) pdf.addPage();

        // Cover
        if (page.layout === "cover") {
          pdf.setFillColor(comic.cover_color || "#0d1a3f");
          pdf.rect(0, 0, W, H, "F");
          pdf.setTextColor("#ffffff");
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(12);
          pdf.text("MUEBLERIA CANALES · CÓMIC", W / 2, 80, { align: "center" });
          pdf.setFontSize(28);
          const title = comic.title[lang] || comic.title.es;
          pdf.text(pdf.splitTextToSize(title, W - M * 2), W / 2, 150, { align: "center" });
          pdf.setFontSize(13);
          pdf.setFont("helvetica", "normal");
          const sub = comic.subtitle[lang] || comic.subtitle.es;
          pdf.text(pdf.splitTextToSize(sub, W - M * 2), W / 2, 220, { align: "center" });
          const cover = illMap[0]?.[0];
          if (cover) {
            try {
              const dataUrl = `data:${cover.mime_type};base64,${cover.data}`;
              pdf.addImage(dataUrl, "JPEG", M, 300, W - M * 2, 420, undefined, "FAST");
              pdf.setDrawColor(0, 0, 0);
              pdf.setLineWidth(3);
              pdf.rect(M, 300, W - M * 2, 420);
            } catch (_e) { /* skip */ }
          }
          return;
        }

        pdf.setTextColor("#0d1a3f");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text(`p. ${idx + 1}`, W - M, M + 4, { align: "right" });
        pdf.setFontSize(18);
        const chapter = page.title?.[lang] || page.title?.es || `Página ${idx + 1}`;
        pdf.text(chapter, M, M + 16);

        // Determine scenes: explicit page.scenes OR derived from panels
        const explicit = Array.isArray(page.scenes) && page.scenes.length > 0 ? page.scenes : null;
        const derived = !explicit ? deriveScenes(comic, page, idx) : null;
        const scenes = explicit || derived;

        const contentTop = M + 32;
        const contentBottom = H - M - 20;

        if (scenes && scenes.length >= 2) {
          // Multi-panel comic-book grid on ONE PDF page.
          const cols = scenes.length >= 3 ? 2 : 1;
          const rows = Math.ceil(scenes.length / cols);
          const gap = 12;
          const cellW = (W - M * 2 - gap * (cols - 1)) / cols;
          const cellH = (contentBottom - contentTop - gap * (rows - 1)) / rows;
          scenes.forEach((sc, si) => {
            const col = si % cols;
            const row = Math.floor(si / cols);
            const cx = M + col * (cellW + gap);
            const cy = contentTop + row * (cellH + gap);
            const ill = illMap[idx]?.[si === 0 ? 0 : si] || illMap[idx]?.[si + 1];
            const dataUrl = ill ? `data:${ill.mime_type};base64,${ill.data}` : null;
            const bubbles = sc.panel ? [{
              speaker: sc.panel.speaker,
              text: sc.panel.text?.[lang] || sc.panel.text?.es || "",
              side: si % 2 === 0 ? "left" : "right",
            }] : [];
            renderImagePanel(dataUrl, cx, cy, cellW, cellH - 24, {
              chapter: si === 0 ? chapter : null,
              bubbles,
              caption: sc.caption ? (sc.caption[lang] || sc.caption.es) : null,
            });
          });
        } else {
          // Single hero image with bubbles from panels
          const bubbles = (page.panels || [])
            .filter((p) => p.speaker && p.speaker !== "narrator")
            .map((p, i) => ({
              speaker: p.speaker,
              text: p.text?.[lang] || p.text?.es || "",
              side: i % 2 === 0 ? "left" : "right",
            }))
            .slice(0, 3);
          const hero = illMap[idx]?.[0];
          const dataUrl = hero ? `data:${hero.mime_type};base64,${hero.data}` : null;
          const heroH = 430;
          renderImagePanel(dataUrl, M, contentTop, W - M * 2, heroH, {
            chapter,
            bubbles,
            caption: page.caption ? (page.caption[lang] || page.caption.es) : null,
          });
          // Lesson under
          if (page.lesson) {
            const y0 = contentTop + heroH + 40;
            const lTitle = page.lesson.title?.[lang] || page.lesson.title?.es || "";
            const lText = page.lesson.text?.[lang] || page.lesson.text?.es || "";
            pdf.setFillColor(255, 209, 0);
            const lines = pdf.splitTextToSize(lText, W - M * 2 - 16);
            const boxH = 30 + lines.length * 12 + 8;
            pdf.rect(M, y0, W - M * 2, boxH, "F");
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(2);
            pdf.rect(M, y0, W - M * 2, boxH);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(12);
            pdf.setTextColor("#0d1a3f");
            pdf.text(lTitle, M + 8, y0 + 18);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);
            pdf.text(lines, M + 8, y0 + 34);
          }
        }

        pdf.setFontSize(8);
        pdf.setTextColor(120, 120, 120);
        pdf.setFont("helvetica", "italic");
        pdf.text("Muebleria Canales — Aprende, diviértete y gana dinero", W / 2, H - 12, { align: "center" });
      });

      const safeSlug = comic.slug.replace(/[^a-z0-9]/gi, "-");
      pdf.save(`canales-comic-${safeSlug}.pdf`);
      toast.success(lang === "es" ? "PDF descargado" : "PDF downloaded");
    } catch (e) {
      toast.error(lang === "es" ? "Error al generar PDF" : "PDF error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={generate}
      disabled={busy}
      data-testid={testid}
      className="brutal-btn bg-white text-black px-4 py-2 flex items-center gap-2 font-display font-bold text-sm disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {busy ? (lang === "es" ? "Generando…" : "Generating…") : "PDF"}
    </button>
  );
}
